import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project.service';
import { scheduleService } from '../../services/schedule.service';
import { getUserById } from '../../services/user.service';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Project } from '../../types/project.types';
import { Schedule } from '../../types/schedule.types';
import AppointmentsList from '../../components/schedule/AppointmentsList';
import { HourlyAvailabilityGrid, DateAvailability } from '../../components/schedule/HourlyAvailabilityGrid';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Calendar, Clock, Users } from 'lucide-react';

interface ClientUser {
  userId: string;
  name: string;
  email: string;
  role: string;
}

export const ProjectSchedulePage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'appointments' | 'availability'>('appointments');

  // Client users and schedule editing
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientAvailability, setSelectedClientAvailability] = useState<DateAvailability | null>(null);
  const [savingAvailability, setSavingAvailability] = useState(false);

  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  useEffect(() => {
    if (projectId) {
      loadProjectData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProjectData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      // Load project details
      const projectData = await projectService.getProject(projectId);
      setProject(projectData);

      // Load schedules for this project
      const projectSchedules = await scheduleService.getSchedulesByProject(projectId);
      setSchedules(projectSchedules);

      // Load client users by querying users who have this project in their projects array
      // This is more reliable than project.assignedUsers which may be out of sync
      const usersQuery = query(
        collection(db, 'users'),
        where('projects', 'array-contains', projectId)
      );
      const usersSnapshot = await getDocs(usersQuery);

      const clients: ClientUser[] = [];
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        // Filter for client roles
        if (userData.role === 'client_user' || userData.role === 'client_admin') {
          clients.push({
            userId: doc.id,
            name: userData.name || userData.email || 'Unknown',
            email: userData.email || '',
            role: userData.role
          });
        }
      });

      setClientUsers(clients);

      // Auto-select if only one client
      if (clients.length === 1) {
        handleSelectClient(clients[0].userId, projectSchedules);
      }
    } catch (error) {
      console.error('Error loading project data:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = (clientUserId: string, currentSchedules?: Schedule[]) => {
    setSelectedClientId(clientUserId);

    if (!clientUserId) {
      setSelectedClientAvailability(null);
      return;
    }

    const schedulesToUse = currentSchedules || schedules;
    const clientSchedule = schedulesToUse.find(s => s.userId === clientUserId);

    if (clientSchedule) {
      if (clientSchedule.dateAvailability) {
        setSelectedClientAvailability(clientSchedule.dateAvailability);
      } else if (clientSchedule.hourlyAvailability) {
        setSelectedClientAvailability(clientSchedule.hourlyAvailability as any);
      } else {
        setSelectedClientAvailability(null);
      }
    } else {
      setSelectedClientAvailability(null);
    }
  };

  const handleSaveAvailability = async (availability: DateAvailability) => {
    if (!selectedClientId || !projectId) return;

    const selectedClient = clientUsers.find(c => c.userId === selectedClientId);
    if (!selectedClient) return;

    setSavingAvailability(true);
    try {
      await scheduleService.saveDateAvailability(
        selectedClientId,
        projectId,
        selectedClient.name,
        availability
      );
      setSelectedClientAvailability(availability);
      toast.success(`Schedule updated for ${selectedClient.name}`);

      // Reload schedules
      const projectSchedules = await scheduleService.getSchedulesByProject(projectId);
      setSchedules(projectSchedules);
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to save schedule');
      throw error;
    } finally {
      setSavingAvailability(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Project not found</p>
        <button
          onClick={() => navigate('/projects')}
          className="mt-4 text-indigo-600 hover:text-indigo-800"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Schedule</h1>
            <p className="text-gray-600">{project.name}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('appointments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'appointments'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Calendar className="h-4 w-4" />
            Appointments
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'availability'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="h-4 w-4" />
            Availability
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'appointments' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Upcoming Appointments</h2>
          <AppointmentsList
            projectId={projectId || ''}
            limit={20}
            showActions={true}
          />
        </div>
      )}

      {activeTab === 'availability' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-medium text-gray-900">Client Availability</h2>
          </div>

          {clientUsers.length > 0 ? (
            <>
              {/* Client Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client to {isProvider ? 'Edit' : 'View'} Schedule
                </label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleSelectClient(e.target.value)}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- Select a client --</option>
                  {clientUsers.map((client) => (
                    <option key={client.userId} value={client.userId}>
                      {client.name} ({client.role === 'client_admin' ? 'Admin' : 'User'})
                    </option>
                  ))}
                </select>
                {isProvider && (
                  <p className="text-xs text-gray-500 mt-1">
                    As a provider, you can update client schedules on their behalf.
                  </p>
                )}
              </div>

              {/* Availability Grid */}
              {selectedClientId ? (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    {isProvider ? 'Editing' : 'Viewing'} schedule for: <strong>{clientUsers.find(c => c.userId === selectedClientId)?.name}</strong>
                  </p>
                  <HourlyAvailabilityGrid
                    initialAvailability={selectedClientAvailability || undefined}
                    onSave={isProvider ? handleSaveAvailability : undefined}
                    saving={savingAvailability}
                    readOnly={!isProvider}
                    userName={clientUsers.find(c => c.userId === selectedClientId)?.name}
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a client above to view or edit their schedule.</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No client users assigned to this project yet.</p>
              <p className="text-sm mt-2">
                Assign client users to the project to manage their availability.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectSchedulePage;
